"use client";
import { getApiBaseUrl } from "@/lib/apiBase";
import { fetchJsonArray } from "@/lib/fetchJsonSafe";

import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { Star, User, ArrowRight, ThumbsUp, Heart, Smile, AlertCircle } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { partnerReplyLabel } from "@/lib/partnerReplyLabel";
const BASE_URL = getApiBaseUrl();

function buildSafeImageUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  const value = raw.trim();
  if (!value || value === "null" || value === "undefined") return null;
  if (/^data:/i.test(value)) return value;
  if (/^https?:\/\//i.test(value)) return value;
  const path = value.startsWith("/") ? value : `/${value}`;
  return BASE_URL ? `${BASE_URL}${path}` : path;
}

function shouldUnoptimizeImage(src) {
  return typeof src === "string" && /^data:/i.test(src);
}

function ReviewCard({ review, getCardBackground, getStarSquareBackground, getRatingColor, userReactions = {}, onReactionClick, session, onShowLoginModal }) {
  // Check if this is the user's own review (only for customers)
  const isOwnReview = session?.id && session?.type === 'customer' && review.reviewer_id && review.reviewer_id === session.id;
  const [imageError, setImageError] = useState(false);
  const [localReactions, setLocalReactions] = useState({
    helpful: review.reactions?.helpful || 0,
    thanks: review.reactions?.thanks || 0,
    love: review.reactions?.love || 0,
    ohno: review.reactions?.ohno || 0,
  });
  const [userReaction, setUserReaction] = useState(userReactions?.[review.id] || review.userReaction || null);
  const companyId = review.company?.id;

  // Update userReaction when review.userReaction changes
  useEffect(() => {
    setUserReaction(userReactions?.[review.id] || review.userReaction || null);
  }, [review.userReaction, userReactions, review.id]);

  const handleCardClick = (e) => {
    // Don't navigate if clicking on emotion buttons
    if (e.target.closest('.emotion-button')) {
      return;
    }
    if (companyId) {
      window.location.href = `/partner/${companyId}`;
    }
  };

  const handleEmotionClick = async (e, emotionType) => {
    e.stopPropagation();

    const customerId =
      session?.id && (!session?.type || session?.type === "customer") ? session.id : null;
    if (!customerId) {
      if (onShowLoginModal) {
        onShowLoginModal();
      } else {
        alert("Сэтгэгдэл үлдээхийн тулд нэвтэрнэ үү");
      }
      return;
    }

    // Toggle reaction
    const newReaction = userReaction === emotionType ? null : emotionType;

    // Optimistic update
    const prevReaction = userReaction;
    setUserReaction(newReaction);

    // Update local counts
    setLocalReactions((prev) => {
      const updated = { ...prev };
      // Remove previous reaction
      if (prevReaction && prevReaction !== newReaction) {
        updated[prevReaction] = Math.max(0, updated[prevReaction] - 1);
      }
      // Add new reaction
      if (newReaction) {
        updated[newReaction] = (updated[newReaction] || 0) + (prevReaction === newReaction ? -1 : 1);
      } else if (prevReaction) {
        updated[prevReaction] = Math.max(0, updated[prevReaction] - 1);
      }
      return updated;
    });

    // Call API
    try {
      const response = await fetch("/api/review/reaction", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(session?.accessToken
            ? { Authorization: `Bearer ${session.accessToken}` }
            : {}),
        },
        body: JSON.stringify({
          review_id: review.id,
          reaction_type: newReaction,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        if (err?.error) {
          alert(err.error);
        }
        // Revert on error
        setUserReaction(prevReaction);
        setLocalReactions({
          helpful: review.reactions?.helpful || 0,
          thanks: review.reactions?.thanks || 0,
          love: review.reactions?.love || 0,
          ohno: review.reactions?.ohno || 0,
        });
      } else {
        const data = await response.json();
        setLocalReactions({
          helpful: data.reactions?.helpful || 0,
          thanks: data.reactions?.thanks || 0,
          love: data.reactions?.love || 0,
          ohno: data.reactions?.ohno || 0,
        });
        setUserReaction(data.userReaction || null);
        // Update parent component
        if (onReactionClick) {
          onReactionClick(review.id, data.userReaction || null);
        }
      }
    } catch (error) {
      console.error('Error updating reaction:', error);
      // Revert on error
      setUserReaction(prevReaction);
      setLocalReactions({
        helpful: review.reactions?.helpful || 0,
        thanks: review.reactions?.thanks || 0,
        love: review.reactions?.love || 0,
        ohno: review.reactions?.ohno || 0,
      });
    }
  };

  const emotions = [
    { type: 'helpful', label: 'Helpful', icon: ThumbsUp, color: 'text-blue-600', bgColor: 'bg-blue-100', borderColor: 'border-blue-400', activeColor: 'text-blue-700' },
    { type: 'thanks', label: 'Thanks', icon: Smile, color: 'text-green-600', bgColor: 'bg-green-100', borderColor: 'border-green-400', activeColor: 'text-green-700' },
    { type: 'love', label: 'Love this', icon: Heart, color: 'text-red-600', bgColor: 'bg-red-100', borderColor: 'border-red-400', activeColor: 'text-red-700' },
    { type: 'ohno', label: 'Oh no', icon: AlertCircle, color: 'text-orange-600', bgColor: 'bg-orange-100', borderColor: 'border-orange-400', activeColor: 'text-orange-700' },
  ];

  return (
    <div
      onClick={handleCardClick}
      className={`${getCardBackground()} rounded-lg p-5 hover:shadow-2xl transition-shadow border border-slate-200 cursor-pointer flex flex-col h-full`}
    >
      {/* Content Section - Flex grow to push company to bottom */}
      <div className="flex-1">
        {/* Reviewer Info */}
        <div className="flex items-center gap-3 mb-3">
          {review.reviewer.avatar && !imageError ? (
            <Image
              src={review.reviewer.avatar}
              alt={review.reviewer.name}
              width={40}
              height={40}
              className="rounded-full object-cover"
              unoptimized={shouldUnoptimizeImage(review.reviewer.avatar)}
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600">
              <User className="w-6 h-6" />
            </div>
          )}
          <span className="text-sm font-medium text-slate-800">
            {review.reviewer.name}
          </span>
        </div>

        {/* Star Rating */}
        <div className="flex items-center gap-1 mb-3">
          {[...Array(5)].map((_, i) => {
            const isFilled = i < review.rating;
            return (
              <div
                key={i}
                className={`w-7 h-7 rounded-sm ${isFilled
                    ? getStarSquareBackground(review.rating)
                    : "bg-slate-200"
                  } flex items-center justify-center`}
              >
                <Star
                  className={`h-4 w-4 ${isFilled
                      ? `fill-current ${getRatingColor(review.rating)}`
                      : "text-white fill-current"
                    }`}
                />
              </div>
            );
          })}
        </div>

        {/* Review Text */}
        <div className="mb-4 min-h-[5rem]">
          <p className="text-sm text-slate-600 line-clamp-4 overflow-hidden text-ellipsis">
            {review.text || "Сэтгэгдэл байхгүй"}
          </p>
          {review.partnerReply ? (
            <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50/70 px-3 py-2">
              <p className="text-[11px] font-semibold text-blue-700">{partnerReplyLabel(review)}</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-700">{review.partnerReply}</p>
            </div>
          ) : null}
        </div>
      </div>

      {/* Emotion Buttons - Disabled if this is the user's own review */}
      <div className="flex items-center gap-1.5 mb-4 pt-4 border-t border-slate-200">
        {emotions.map((emotion) => {
          const Icon = emotion.icon;
          const isActive = userReaction === emotion.type;
          const count = localReactions[emotion.type] || 0;
          const hasCount = count > 0;
          const showActive = isActive || hasCount; // Active if clicked OR has count

          return (
            <button
              key={emotion.type}
              onClick={(e) => {
                if (!isOwnReview) {
                  handleEmotionClick(e, emotion.type);
                }
              }}
              disabled={isOwnReview}
              className={`emotion-button group relative flex items-center gap-1 px-2 py-1 rounded-lg transition-all ${isOwnReview
                  ? 'opacity-50 cursor-not-allowed bg-white text-slate-400'
                  : showActive
                    ? `${emotion.bgColor} ${emotion.activeColor || emotion.color}`
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              title={emotion.label}
            >
              <Icon className={`w-4 h-4 ${showActive ? 'fill-current' : ''}`} />
              {count > 0 && (
                <span className={`text-xs font-bold ${showActive ? (emotion.activeColor || emotion.color) : 'text-slate-500'}`}>
                  {count}
                </span>
              )}
              {/* Tooltip on hover */}
              <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-slate-900 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                {emotion.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Company Info - Always at bottom with border line */}
      <div className="flex items-center gap-3 pt-4 mt-auto border-t border-slate-200">
        <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center text-sm font-semibold text-slate-600 flex-shrink-0 overflow-hidden relative">
          {review.company.logo ? (
            <Image
              src={review.company.logo}
              alt={review.company.name}
              fill
              className="rounded-lg object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentElement.textContent = review.company.name[0];
              }}
              unoptimized={shouldUnoptimizeImage(review.company.logo)}
            />
          ) : (
            review.company.name[0]
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">
            {review.company.name}
          </p>
          <p className="text-xs text-slate-500 truncate">
            {review.company.website}
          </p>
        </div>
      </div>
    </div>
  );
}

function LoginModal({ isOpen, onClose, onLogin }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm transform transition-all scale-100 opacity-100 animate-in fade-in zoom-in duration-200">
        <div className="flex flex-col items-center text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>

          <h3 className="text-xl font-bold text-slate-900 mb-2">
            Нэвтрэх шаардлагатай
          </h3>

          <p className="text-slate-600 mb-6">
            Сэтгэгдэл үлдээхийн тулд та системд нэвтэрнэ үү.
          </p>

          <div className="flex gap-3 w-full">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
            >
              Болих
            </button>
            <button
              onClick={onLogin}
              className="flex-1 px-4 py-2.5 rounded-xl bg-[#005A8B] text-white font-medium hover:bg-[#004870] transition-colors shadow-lg shadow-blue-900/20"
            >
              Нэвтрэх
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RecentReviews() {
  const { data: session } = useSession();
  const router = useRouter();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userReactions, setUserReactions] = useState({}); // { reviewId: reactionType }

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const data = await fetchJsonArray(`/api/review?type=recent`);

        // Transform API data to match component structure
        const transformedReviews = data.map((review) => ({
          id: review.id,
          rating: review.rating,
          text: review.content,
          partnerReply: review.partnerReply || null,
          partnerRepliedAt: review.partnerRepliedAt || null,
          reviewer_id: review.reviewer_id || review.reviewer?.id || null,
          reactions: review.reactions || {
            helpful: 0,
            thanks: 0,
            love: 0,
            ohno: 0,
          },
          userReaction: review.userReaction || null,
          reviewer: {
            name: review.reviewer?.name || "Хэрэглэгч",
            avatar: buildSafeImageUrl(review.reviewer?.image),
            initial: review.reviewer?.name?.[0]?.toUpperCase() || "U",
          },
          company: {
            id: review.company?.id || null,
            name: review.company?.company_name || "Компани",
            logo:
              buildSafeImageUrl(review.company?.image) ||
              (review.company?.company_name
                ? `https://logo.clearbit.com/${review.company.company_name.toLowerCase().replace(/\s+/g, "")}.com`
                : null),
            website: review.company?.website || "",
          },
        }));

        // Build user reactions map
        const reactionsMap = {};
        transformedReviews.forEach((review) => {
          if (review.userReaction) {
            reactionsMap[review.id] = review.userReaction;
          }
        });
        setUserReactions(reactionsMap);

        // Limit to 8 reviews (2 rows x 4 items)
        setReviews(transformedReviews.slice(0, 8));
      } catch (error) {
        console.error("Error fetching reviews:", error);
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [session]);

  const handleReactionUpdate = (reviewId, newReaction) => {
    setUserReactions((prev) => ({
      ...prev,
      [reviewId]: newReaction,
    }));

    // Update review reactions count
    setReviews((prevReviews) =>
      prevReviews.map((review) => {
        if (review.id === reviewId) {
          const oldReaction = userReactions[reviewId];
          const newReactions = { ...review.reactions };

          // Remove old reaction
          if (oldReaction) {
            newReactions[oldReaction] = Math.max(0, (newReactions[oldReaction] || 0) - 1);
          }

          // Add new reaction
          if (newReaction) {
            newReactions[newReaction] = (newReactions[newReaction] || 0) + 1;
          }

          return {
            ...review,
            reactions: newReactions,
            userReaction: newReaction,
          };
        }
        return review;
      })
    );
  };

  // Display only 8 reviews (2 rows x 4 items per row)
  const displayedReviews = reviews.slice(0, 8);

  if (loading) {
    return (
      <div className="max-w-[1440px] mx-auto py-8 px-4 mt-5">
        <div className="mb-10">
          <div className="h-10 bg-gray-200 rounded-lg animate-pulse w-64 mb-6"></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="bg-white rounded-lg p-5 border border-gray-200 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </div>
              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((j) => (
                  <div key={j} className="w-7 h-7 bg-gray-200 rounded-sm"></div>
                ))}
              </div>
              <div className="space-y-2 mb-4">
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              </div>
              <div className="flex gap-1.5 mb-4 pt-4 border-t border-gray-200">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="w-10 h-8 bg-gray-200 rounded-lg"></div>
                ))}
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                <div className="w-14 h-14 bg-gray-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const getRatingColor = (rating) => {
    return "text-white";
  };

  const getCardBackground = () => {
    return "bg-white";
  };

  const getStarSquareBackground = (rating) => {
    if (rating >= 5) return "bg-green-600";
    if (rating >= 4) return "bg-green-400";
    if (rating >= 3) return "bg-yellow-400";
    if (rating >= 2) return "bg-orange-400";
    return "bg-red-600";
  };

  return (
    <div className="max-w-[1440px] mx-auto py-12 px-4 mt-8">
      {/* Header Section */}
      <div className="mb-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-1 w-8 bg-[#577099] rounded-full"></div>
              <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Хамгийн сүүлийн</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
              Сэтгэгдэлүүд
            </h1>
            <p className="mt-1 text-slate-600 text-sm">
              {/* Хэрэглэгчдийн сүүлийн сэтгэгдлүүд */}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              href="/reviews"
              className="flex items-center gap-2 px-6 py-3 bg-[#577099] text-white rounded-full hover:bg-[#4a5f85] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-semibold"
            >
              <span className="text-sm">Бүгдийг харах</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Reviews Grid - 4 columns, 2 rows (8 items total) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 items-stretch">
        {displayedReviews.map((review) => (
          <ReviewCard
            key={review.id}
            review={review}
            getCardBackground={getCardBackground}
            getStarSquareBackground={getStarSquareBackground}
            getRatingColor={getRatingColor}
            userReactions={userReactions}
            onReactionClick={handleReactionUpdate}
            session={session}
            onShowLoginModal={() => setShowLoginModal(true)}
          />

        ))}
      </div>

      {/* Empty State */}
      {displayedReviews.length === 0 && !loading && (
        <div className="text-center py-16">
          <div className="text-slate-400 text-lg">Сэтгэгдэл олдсонгүй</div>
        </div>
      )}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={() => router.push('/customer')}
      />
    </div>
  );
}

